import { useCallback, useState, useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import { createEmailNotificationAction } from "@/features/adminEmailNotifications/slices/emailNotificationSlice";
import { useNotification } from "@/context/NotificationContext";
import { getMailchimpTemplates } from "@/api/getMailchimpTemplates/getMailchimpTemplatesApi";
import { getMailchimpTemplateHtml } from "@/api/getMailchimpTemplateHtml/getMailchimpTemplateHtmlApi";
import { MailchimpTemplate } from "@/api/getMailchimpTemplates/types";

// Define the form schema using Zod
export const emailNotificationFormSchema = z.object({
  subject: z.string().min(1, "Email subject is required"),
  mailchimpTemplateId: z.number({
    required_error: "Template selection is required",
    invalid_type_error: "Template selection is required",
  }),
  customContent: z.string().min(1, "Email content is required"),
});

export type EmailNotificationFormData = z.infer<
  typeof emailNotificationFormSchema
>;

export function useCreateEmailNotification(onClose: () => void) {
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  // Templates state
  const [templates, setTemplates] = useState<MailchimpTemplate[]>([]);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  // Loading and error states for template fetching/preview
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get createLoading and createError from Redux
  const { createLoading, createError } = useSelector((state: RootState) => ({
    createLoading: state.emailNotifications.createLoading,
    createError: state.emailNotifications.createError,
  }));

  // Initialize the form
  const form = useForm<EmailNotificationFormData>({
    resolver: zodResolver(emailNotificationFormSchema),
    defaultValues: {
      subject: "",
      mailchimpTemplateId: undefined,
      customContent: "",
    },
  });

  const { watch } = form;
  const mailchimpTemplateId = watch("mailchimpTemplateId");
  const customContent = watch("customContent");

  // Fetch templates on mount
  const fetchTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getMailchimpTemplates();
      setTemplates(response.data);
    } catch (err) {
      console.error("Error fetching Mailchimp templates:", err);
      setError("Failed to load templates. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle preview generation
  const handlePreview = useCallback(async () => {
    if (!mailchimpTemplateId) {
      setError("Please select a template first");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await getMailchimpTemplateHtml({
        templateId: mailchimpTemplateId,
        customContent,
      });
      setPreviewHtml(response.data.html);
    } catch (err) {
      console.error("Error fetching template HTML:", err);
      setError("Failed to load template preview. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [mailchimpTemplateId, customContent]);

  // Submit form handler
  const submitForm = useCallback(
    async (values: EmailNotificationFormData) => {
      setError(null);
      try {
        await dispatch(
          createEmailNotificationAction({
            mailchimpTemplateId: values.mailchimpTemplateId,
            subject: values.subject,
            customContent: values.customContent,
          }),
        );

        onClose();
        showNotification(
          "Email Sent",
          "Your email was created and queued for delivery!",
          "success",
        );
      } catch (err) {
        console.error("Error creating email notification:", err);
        // Form error is handled by redux state
      }
    },
    [dispatch, onClose, showNotification],
  );

  // Reset form state
  const resetForm = useCallback(() => {
    form.reset();
    setPreviewHtml(null);
  }, [form]);

  return {
    form,
    templates,
    previewHtml,
    isLoading,
    error,
    createLoading,
    createError,
    fetchTemplates,
    handlePreview,
    submitForm,
    resetForm,
  };
}
